import { Trans } from '@lingui/macro'
import { FormControlLabel } from '@mui/material'
import { useDispatch } from 'react-redux'
import { FormError, FormErrorList } from './FormErrorList'
import Checkbox from 'app/views/common-components/CustomCheckbox'

interface TypeProps {
  showAllErrors?: boolean
  showSectionErrors?: boolean
}

interface FormEditorErrorListProps {
  editMode: boolean
  depth: string
  typeProps?: TypeProps
  injectable: Boolean
  errors: FormError[]
  navigateToError: () => void
  sectionIndex: number
  title?: string
}

/**
 * FormEditorErrorList component is responsible for configuring the display of error messages.
 * It supports two modes:
 * - `editMode` enabled: Provides checkboxes to toggle error visibility.
 * - `editMode` disabled: Renders the FormErrorList component with provided errors.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {boolean} props.editMode - Determines whether the form editor is in edit mode.
 * @param {string} props.depth - Represents the hierarchical depth of the current form section.
 * @param {TypeProps} [props.typeProps] - Optional type properties that control error visibility.
 * @param {boolean} props.injectable - Indicates whether the field can be dynamically modified.
 * @param {object[]} props.errors - The list of errors to be displayed.
 * @param {() => void} props.navigateToError - Function to navigate to a given error.
 * @param {number} props.sectionIndex - The index of the current section within the form.
 * @param {string} [props.title] - Message to be displayed as error message; if not passed to
 * the form element, a default message is used.
 *
 * @returns {JSX.Element} The rendered FormEditorErrorList component.
 */
export const FormEditorErrorList = ({
  editMode,
  depth,
  typeProps = {},
  injectable,
  errors = [],
  sectionIndex,
  navigateToError,
  title,
  ...props
}: FormEditorErrorListProps): JSX.Element => {
  const { showAllErrors, showSectionErrors } = typeProps
  const dispatch = useDispatch()

  if (!editMode) {
    return (
      <FormErrorList
        {...props}
        title={title}
        typeProps={typeProps}
        errors={errors}
        sectionIndex={sectionIndex}
        navigateToError={navigateToError}
      />
    )
  }

  return (
    <div>
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(showAllErrors) || !showSectionErrors}
              onChange={e => {
                const toSet = { ...typeProps }
                toSet.showAllErrors = e.target.checked
                delete toSet.showSectionErrors
                dispatch({
                  type: 'FIELD',
                  injectable,
                  depth: depth.split('.'),
                  fieldName: 'typeProps',
                  fieldValue: { ...toSet }
                })
              }}
            />
          }
          label={<Trans>Show all errors?</Trans>}
        />
      </div>
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(showSectionErrors)}
              onChange={e => {
                const toSet = { ...typeProps }
                toSet.showSectionErrors = e.target.checked
                delete toSet.showAllErrors
                dispatch({
                  type: 'FIELD',
                  injectable,
                  depth: depth.split('.'),
                  fieldName: 'typeProps',
                  fieldValue: { ...toSet }
                })
              }}
            />
          }
          label={<Trans>Show errors from current section?</Trans>}
        />
      </div>
    </div>
  )
}
